/**
 * Every way a checkout field can fail, as one closed union. Nothing widens to `string`:
 * a new failure has to be added here, and `fieldErrorMessages` will not compile until it
 * has a translation.
 */
export type FieldErrorCode =
    | "value_missing"
    | "value_invalid"
    | "value_too_long"
    | "email_invalid"
    | "phone_invalid"
    | "address_too_long"
    | "po_box_not_allowed"
    /**
     * Covers non-Latin scripts too. Naming the script would be a guess: the Japanese and
     * Chinese ranges share their ideographs, so the first check to match wins regardless
     * of what was actually typed.
     */
    | "special_characters_not_allowed"
    | "zip_code_format_invalid"
    | "zip_code_state_mismatch"
    | "pfas_restricted"
    /** Pushed in by the Loqate modal rather than raised by a validator. */
    | "address_not_verified"

/**
 * Errors that also hold the submit button down. Everything else marks its field and lets
 * the customer carry on: only a PFAS restriction makes the order itself unsellable, and no
 * amount of correcting the address fixes it.
 */
export const BLOCKING_FIELD_ERROR_CODES: ReadonlySet<FieldErrorCode> =
    new Set<FieldErrorCode>(["pfas_restricted"])

/**
 * Verdicts on a value rather than faults in it. They are shown, but they never fail
 * validation — Loqate not recognising an address is not proof the address is wrong, and
 * the customer can still stand by it — and they go stale the moment the value changes.
 */
export const ADVISORY_FIELD_ERROR_CODES: ReadonlySet<FieldErrorCode> =
    new Set<FieldErrorCode>(["address_not_verified"])

/** The fields whose "you left this blank" wording differs. */
export type CheckoutFieldName =
    | "email"
    | "firstName"
    | "lastName"
    | "address1"
    | "apartmentHouseNumber"
    | "city"
    | "state"
    | "zipCode"
    | "phoneNumber"
    | "country"

const CHECKOUT_FIELD_NAMES: ReadonlySet<string> = new Set<CheckoutFieldName>([
    "email",
    "firstName",
    "lastName",
    "address1",
    "apartmentHouseNumber",
    "city",
    "state",
    "zipCode",
    "phoneNumber",
    "country"
])

/**
 * Inputs are named `shipping_city` / `billing_city` because submit matches on that prefix.
 * Anything outside the address forms falls through to the generic wording.
 */
export const checkoutFieldFromInputName = (
    inputName: string
): CheckoutFieldName | undefined => {
    const field = inputName.includes("_")
        ? inputName.slice(inputName.indexOf("_") + 1)
        : inputName
    return CHECKOUT_FIELD_NAMES.has(field) ? (field as CheckoutFieldName) : undefined
}

export interface FieldValidationContext {
    /** Allowed on top of the default set, e.g. `-` in an address line. */
    allowedCharacters?: string
    countryCode?: string
}

/** Pure: takes the value, returns a code. No DOM, no state, no message. */
export type FieldValidator = (
    value: string,
    context: FieldValidationContext
) => FieldErrorCode | null

export interface FieldError {
    code: FieldErrorCode
    message: string
}
